import { FC, useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  useColorModeValue,
  useToast,
  Spinner,
  Center,
  Badge,
  Divider,
  Card,
  SimpleGrid,
  Avatar,
  Progress,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Input,
  InputGroup,
  InputLeftElement,
  Button
} from '@chakra-ui/react';
import {
  MdPerson,
  MdCalendarToday,
  MdAccessTime,
  MdCheckCircle,
  MdCancel,
  MdWarning,
  MdInfo,
  MdSchool,
  MdTrendingUp,
  MdSearch
} from 'react-icons/md';
import {
  getCourseStudents,
  unenrollStudent,
  CourseStudent
} from '@/services/university-service';

interface CourseStudentsListProps {
  courseId: number;
  onStudentUnenrolled?: (studentId: number) => void;
}

const CourseStudentsList: FC<CourseStudentsListProps> = ({ courseId, onStudentUnenrolled }) => {
  const toast = useToast();
  const [students, setStudents] = useState<CourseStudent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedStudent, setSelectedStudent] = useState<CourseStudent | null>(null);
  const [unenrolling, setUnenrolling] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const {
    isOpen: isUnenrollModalOpen,
    // onOpen: onUnenrollModalOpen,
    onClose: onUnenrollModalClose
  } = useDisclosure();

  const cardBg = useColorModeValue('white', 'gray.800');

  // Load students data
  useEffect(() => {
    const loadStudents = async () => {
      try {
        setLoading(true);
        const response = await getCourseStudents(courseId);
        if (response.statusCode === 200 && response.message) {
          setStudents(response.message as CourseStudent[]);
        } else {
          throw new Error(response.message || 'Failed to load students');
        }
      } catch (error: any) {
        console.error('Error loading students:', error);
        toast({
          title: 'Error loading students',
          description: error.message || 'Failed to load students data.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    loadStudents();
  }, [courseId, toast]);

  const handleUnenrollStudent = async () => {
    if (!selectedStudent) return;
    try {
      setUnenrolling(true);
      const response = await unenrollStudent(courseId, selectedStudent.userId);
      if (response.statusCode === 200) {
        toast({
          title: 'Student unenrolled',
          description: `${selectedStudent.user?.name || 'Student'} has been unenrolled from the course.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setStudents((prev: CourseStudent[]) => prev.filter((student: CourseStudent) => student.id !== selectedStudent.id));
        onStudentUnenrolled?.(selectedStudent.id);
        onUnenrollModalClose();
        setSelectedStudent(null);
      } else {
        throw new Error(response.message || 'Failed to unenroll student');
      }
    } catch (error: any) {
      console.error('Error unenrolling student:', error);
      toast({
        title: 'Error unenrolling student',
        description: error.message || 'Failed to unenroll student.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setUnenrolling(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'enrolled': return 'blue';
      case 'in_progress': return 'orange';
      case 'completed': return 'green';
      case 'dropped': return 'red';
      case 'suspended': return 'yellow';
      default: return 'gray';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'enrolled': return MdPerson;
      case 'in_progress': return MdTrendingUp;
      case 'completed': return MdCheckCircle;
      case 'dropped': return MdCancel;
      case 'suspended': return MdWarning;
      default: return MdInfo;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return 'green';
    if (progress >= 60) return 'orange';
    if (progress >= 40) return 'yellow';
    return 'red';
  };

  // Filter students based on search term
  const filteredStudents = students.filter((student: CourseStudent) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      student.user?.name?.toLowerCase().includes(searchLower) ||
      student.user?.email?.toLowerCase().includes(searchLower)
    );
  });

  // Calculate statistics
  const totalStudents = students.length;
  const inProgressStudents = students.filter((s: CourseStudent) => s.status === 'in_progress').length;
  const averageProgress = totalStudents > 0
    ? Math.round(students.reduce((sum: number, s: CourseStudent) => sum + s.overallProgress, 0) / totalStudents)
    : 0;

  if (loading) {
    return (
      <Center py={8}>
        <VStack spacing={3}>
          <Spinner size="lg" color="blue.500" />
          <Text>Loading students...</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      {/* Statistics */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
        <Box textAlign="center" p={4} bg={cardBg} borderRadius="lg">
          <Text fontSize="2xl" fontWeight="bold" color="blue.500">{totalStudents}</Text>
          <Text fontSize="sm" color="gray.600">Total Students</Text>
        </Box>
        <Box textAlign="center" p={4} bg={cardBg} borderRadius="lg">
          <Text fontSize="2xl" fontWeight="bold" color="orange.500">{inProgressStudents}</Text>
          <Text fontSize="sm" color="gray.600">In Progress</Text>
        </Box>
        <Box textAlign="center" p={4} bg={cardBg} borderRadius="lg">
          <Text fontSize="2xl" fontWeight="bold" color="green.500">{averageProgress}%</Text>
          <Text fontSize="sm" color="gray.600">Average Progress</Text>
        </Box>
      </SimpleGrid>

      {/* Search */}
      <InputGroup>
        <InputLeftElement pointerEvents="none">
          <Icon as={MdSearch} color="gray.300" />
        </InputLeftElement>
        <Input
          placeholder="Search students by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </InputGroup>

      {/* Students List */}
      {students.length === 0 ? (
        <Card bg={cardBg} p={6}>
          <Center>
            <VStack spacing={3}>
              <Icon as={MdSchool} boxSize={8} color="gray.400" />
              <Text color="gray.500">No students enrolled</Text>
              <Text fontSize="sm" color="gray.400">Students will appear here once they enroll in the course.</Text>
            </VStack>
          </Center>
        </Card>
      ) : (
        <VStack align="stretch" spacing={4}>
          {filteredStudents.map((student: CourseStudent) => (
            <Card key={student.id} bg={cardBg} p={4}>
              <VStack align="stretch" spacing={4}>
                {/* Student Header */}
                <HStack justify="space-between" align="start">
                  <HStack spacing={4}>
                    <Avatar
                      size="md"
                      name={student.user?.name || 'Student'}
                      src={student.user?.email ? `https://ui-avatars.com/api/?name=${encodeURIComponent(student.user.name)}&background=random` : undefined}
                    />
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="semibold" fontSize="lg">
                        {student.user?.name || 'Anonymous Student'}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {student.user?.email || 'No email provided'}
                      </Text>
                      <HStack spacing={2}>
                        <Badge
                          colorScheme={getStatusColor(student.status)}
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Icon as={getStatusIcon(student.status)} boxSize={3} mr={1} />
                          {student.status.replace('_', ' ').toUpperCase()}
                        </Badge>
                        {student.grade && (
                          <Badge colorScheme="purple">
                            Grade: {student.grade}%
                          </Badge>
                        )}
                      </HStack>
                    </VStack>
                  </HStack>

                  {/* <HStack spacing={2}>
                    <Tooltip label="View Details">
                      <IconButton
                        size="sm"
                        variant="ghost"
                        icon={<Icon as={MdVisibility} />}
                        aria-label="View student details"
                      />
                    </Tooltip>
                    <Tooltip label="Edit Student">
                      <IconButton
                        size="sm"
                        variant="ghost"
                        icon={<Icon as={MdEdit} />}
                        aria-label="Edit student"
                      />
                    </Tooltip>
                    <Tooltip label="Unenroll Student">
                      <IconButton
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        icon={<Icon as={MdDelete} />}
                        onClick={() => {
                          setSelectedStudent(student
                          );
                          onUnenrollModalOpen();
    
                          }}
// 
                       aria-label="Unenro
                       l student"
                      />
                    </Tooltip>
                  </HStack> */}
                </HStack>

                <Divider />

                {/* Progress Information */}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" mb={2}>Overall Progress</Text>
                    <Progress
                      value={student.overallProgress}
                      colorScheme={getProgressColor(student.overallProgress)}
                      size="lg"
                      borderRadius="full"
                      mb={2}
                    />
                    <Text fontSize="sm" color="gray.600">
                      {student.overallProgress}% complete
                    </Text>
                  </Box>

                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" mb={2}>Modules Progress</Text>
                    <Text fontSize="sm" color="gray.600">
                      {student.completedModules}/{student.totalModules} modules completed
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {student.completedContents}/{student.totalContents} contents completed
                    </Text>
                  </Box>
                </SimpleGrid>

                {/* Additional Details */}
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                  <Box>
                    <HStack spacing={2} mb={1}>
                      <Icon as={MdCalendarToday} color="gray.500" />
                      <Text fontSize="xs" color="gray.500">Enrolled</Text>
                    </HStack>
                    <Text fontSize="sm">
                      {student.enrolledAt ? formatDate(student.enrolledAt) : 'Unknown'}
                    </Text>
                  </Box>

                  <Box>
                    <HStack spacing={2} mb={1}>
                      <Icon as={MdAccessTime} color="gray.500" />
                      <Text fontSize="xs" color="gray.500">Last Access</Text>
                    </HStack>
                    <Text fontSize="sm">
                      {student.lastAccessedAt ? formatDate(student.lastAccessedAt) : 'Never'}
                    </Text>
                  </Box>

                  <Box>
                    <HStack spacing={2} mb={1}>
                      <Icon as={MdAccessTime} color="gray.500" />
                      <Text fontSize="xs" color="gray.500">Time Spent</Text>
                    </HStack>
                    <Text fontSize="sm">
                      {formatDuration(student.totalTimeSpent)}
                    </Text>
                  </Box>
                </SimpleGrid>

                {/* Notes */}
                {student.notes && (
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" mb={1}>Notes</Text>
                    <Text fontSize="sm" color="gray.600" fontStyle="italic">
                      {student.notes}
                    </Text>
                  </Box>
                )}
              </VStack>
            </Card>
          ))}
        </VStack>
      )}

      {/* Unenroll Confirmation Modal */}
      <Modal isOpen={isUnenrollModalOpen} onClose={onUnenrollModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Unenroll Student</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="warning" mb={4}>
              <AlertIcon />
              <Box>
                <AlertTitle>Warning!</AlertTitle>
                <AlertDescription>
                  This action will permanently remove the student from this course.
                  All their progress data will be lost.
                </AlertDescription>
              </Box>
            </Alert>
            <Text>
              Are you sure you want to unenroll{' '}
              <strong>{selectedStudent?.user?.name || 'this student'}</strong>
              from this course?
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onUnenrollModalClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleUnenrollStudent}
              isLoading={unenrolling}
              loadingText="Unenrolling..."
            >
              Unenroll Student
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default CourseStudentsList; 